import type { DocumentResult, PageResult } from '../shared/desktop-api';

export type DocumentSourceIdentity = Pick<
  DocumentResult,
  'sourceName' | 'sourcePath' | 'sourcePaths'
>;

const uniquePaths = (paths: Iterable<string | null | undefined>) => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const candidate of paths) {
    const value = candidate?.trim();
    if (!value) continue;
    const key = value.replaceAll('/', '\\').toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(value);
  }
  return result;
};

const basename = (value: string) =>
  value.replaceAll('\\', '/').split('/').filter(Boolean).at(-1) ?? value;

export const documentSourceIdentity = (
  paths: Iterable<string | null | undefined>,
  fallbackName: string,
): DocumentSourceIdentity => {
  const sourcePaths = uniquePaths(paths);
  const firstName = sourcePaths[0] ? basename(sourcePaths[0]) : fallbackName;
  return {
    sourcePath: sourcePaths[0] ?? '',
    sourcePaths,
    sourceName:
      sourcePaths.length > 1 ? `${firstName} + ${sourcePaths.length - 1} files` : firstName,
  };
};

const previewFolder = (page: PageResult) => {
  const normalized = page.previewPath.replaceAll('\\', '/');
  return normalized.slice(0, Math.max(0, normalized.lastIndexOf('/'))).toLocaleLowerCase();
};

const sourceFolderNumber = (page: PageResult) => {
  const match = /(?:^|[\\/])source-(\d+)(?:[\\/]|$)/iu.exec(page.previewPath);
  return match ? Number.parseInt(match[1], 10) : null;
};

/**
 * Resolve the source file that physically contributed each page.
 *
 * New batches carry this provenance on the page itself. The work-folder fallback
 * exists for batches created before that contract was added: the pipeline writes
 * every source into one ordered `source-0001` folder and preserves the same order
 * in `DocumentResult.sourcePaths`.
 */
export const pageSourcePaths = (document: DocumentResult): (string | null)[] => {
  const candidates = uniquePaths(
    document.sourcePaths?.length ? document.sourcePaths : [document.sourcePath],
  );
  if (document.pages.every((page) => Boolean(page.sourcePath?.trim())))
    return document.pages.map((page) => page.sourcePath!.trim());
  if (candidates.length <= 1) return document.pages.map(() => candidates[0] ?? null);

  const numbered = document.pages.map(sourceFolderNumber);
  if (numbered.every((number) => number !== null && number >= 1 && number <= candidates.length))
    return numbered.map((number) => candidates[number! - 1]);

  const folders = uniquePaths(document.pages.map(previewFolder));
  if (folders.length !== candidates.length) return document.pages.map(() => null);
  const byFolder = new Map(folders.map((folder, index) => [folder, candidates[index]]));
  return document.pages.map((page) => byFolder.get(previewFolder(page)) ?? null);
};

export const partitionDocumentSources = (
  document: DocumentResult,
  pageIndex: number,
): { first: DocumentSourceIdentity; second: DocumentSourceIdentity } | null => {
  if (pageIndex <= 0 || pageIndex >= document.pages.length) return null;
  const pageSources = pageSourcePaths(document);
  const firstPaths = pageSources.slice(0, pageIndex);
  const secondPaths = pageSources.slice(pageIndex);
  if (firstPaths.some((path) => !path) || secondPaths.some((path) => !path)) return null;
  return {
    first: documentSourceIdentity(firstPaths, document.sourceName),
    second: documentSourceIdentity(secondPaths, `${document.sourceName} · split`),
  };
};

export const mergeDocumentSources = (
  documents: readonly DocumentResult[],
  fallbackName: string,
): { identity: DocumentSourceIdentity; pageSources: string[] } | null => {
  const pageSources = documents.flatMap(pageSourcePaths);
  if (pageSources.some((source) => !source)) return null;
  const resolved = pageSources as string[];
  return { identity: documentSourceIdentity(resolved, fallbackName), pageSources: resolved };
};
